import { Component, EventEmitter, Input, Output, inject } from '@angular/core';
import { ActivatedRoute, RouterLink } from '@angular/router';
import { ProjectWorkflowProgressService, ProjectStep } from '../../../core/services/project-workflow-progress.service';

export type ProjectPhase = 'profile' | 'prefeasibility' | 'feasibility';
export interface ProjectStory {
  id: string;
  hu: string;
  label: string;
  phase: ProjectPhase;
  path?: string;
  query?: Record<string, string>;
  progress?: ProjectStep | 'social' | 'chain' | 'funding';
}

export const PROJECT_STORIES: readonly ProjectStory[] = [
  { id: 'record', hu: 'HU-007.0', label: 'Ficha inicial del registro del proyecto', phase: 'profile', path: '/instrumento/registro', progress: 'record' },
  { id: 'geography', hu: 'HU-007', label: 'Validación del área del proyecto', phase: 'profile' },
  { id: 'focalization', hu: 'HU-008', label: 'Focalización', phase: 'profile' },
  { id: 'problems', hu: 'HU-009', label: 'Árbol de problemas', phase: 'profile', path: '/psa/formulacion', query: { section: 'problems' }, progress: 'problems' },
  { id: 'actors', hu: 'HU-010', label: 'Actores', phase: 'profile', path: '/psa/formulacion', query: { section: 'actors' }, progress: 'actors' },
  { id: 'objectives', hu: 'HU-011', label: 'Árbol de objetivos', phase: 'profile', path: '/instrumento/objetivos', progress: 'objectives' },
  { id: 'priority-area', hu: 'HU-012', label: 'Área prioritaria', phase: 'prefeasibility' },
  { id: 'socioeconomic', hu: 'HU-013', label: 'Caracterización socioeconómica', phase: 'prefeasibility' },
  { id: 'incentive', hu: 'HU-014', label: 'Estimación del valor del incentivo', phase: 'prefeasibility' },
  { id: 'chain', hu: 'HU-015', label: 'Cadena de valor', phase: 'prefeasibility', path: '/instrumento/cadena-valor', progress: 'chain' },
  { id: 'funding', hu: 'HU-016', label: 'Fuentes de financiación', phase: 'prefeasibility', path: '/instrumento/financiacion', progress: 'funding' },
  { id: 'cost-benefit', hu: 'HU-017', label: 'Análisis costo-beneficio', phase: 'feasibility' },
  { id: 'concertation', hu: 'HU-018', label: 'Concertación de actores / jurídico', phase: 'feasibility' },
  { id: 'operating-plan', hu: 'HU-019', label: 'Plan operativo', phase: 'feasibility' },
  { id: 'validation', hu: 'HU-020', label: 'Validación de proyecto', phase: 'feasibility' }
];

@Component({
  selector: 'sr-project-stages', standalone: true, imports: [RouterLink],
  templateUrl: './project-stages.component.html', styleUrl: './project-stages.component.css'
})
export class ProjectStagesComponent {
  readonly progress = inject(ProjectWorkflowProgressService);
  private readonly route = inject(ActivatedRoute);
  @Input() projectId: string | null = null;
  @Input() active = 'instrument';
  @Input() busy = false;
  @Input() dirty = false;
  @Input() localRecord = false;
  @Input() localActors = false;
  @Output() recordSelected = new EventEmitter<'instrument' | 'record'>();
  @Output() actorsSelected = new EventEmitter<'problems' | 'actors'>();
  readonly phases = [
    { id: 'profile' as const, label: 'Perfil', icon: '⌖' },
    { id: 'prefeasibility' as const, label: 'Prefactibilidad', icon: '✎' },
    { id: 'feasibility' as const, label: 'Factibilidad', icon: '◎' }
  ];
  get phase(): ProjectPhase | null {
    if (this.active === 'social') return 'prefeasibility';
    return PROJECT_STORIES.find(story => story.id === this.active)?.phase ?? null;
  }
  get stories() { return PROJECT_STORIES.filter(story => story.phase === this.phase); }
  firstStory(phase: ProjectPhase) { return PROJECT_STORIES.find(story => story.phase === phase)!; }
  path(story: ProjectStory) { return story.path ?? '/instrumento/etapa'; }
  params(story?: ProjectStory): Record<string, string | null> {
    return { projectId: this.projectId, mode: this.route.snapshot.queryParamMap.get('mode'),
      ...(story ? story.path ? story.query : { story: story.id } : { stage: 'instrument' }) };
  }
  completed(phase: ProjectPhase): boolean {
    return PROJECT_STORIES.filter(story => story.phase === phase).every(story =>
      !!story.progress && this.progress.completed(this.projectId, story.progress)
    ) && !(this.phase === phase && this.dirty);
  }
  select(event: MouseEvent, id: string): void {
    if (this.busy || (!this.projectId && !(this.localRecord && (id === 'instrument' || id === 'record')))) {
      event.preventDefault(); return;
    }
    if (this.localRecord && (id === 'instrument' || id === 'record')) {
      event.preventDefault(); this.recordSelected.emit(id);
    } else if (this.localActors && (id === 'problems' || id === 'actors')) {
      event.preventDefault(); this.actorsSelected.emit(id);
    }
  }
}
